import { FPFNInfo } from "types/testTypes";
import {
  ReliabilityAutoRestartRule,
  VisualFieldAutoRestartConfiguration,
} from "types/adminConfigurationTypes";
import { DEFAULT_ADMIN_CONFIGURATIONS } from "@constants/adminConfigurationConstants";

export const VFA_RELIABILITY_MAX_AUTO_RESTARTS = 3;

export interface VfaReliabilitySnapshot {
  fpFnInfo?: FPFNInfo | null;
  blindSpotSeen?: number | null;
  blindSpotTotal?: number | null;
}

interface ReliabilityCounter {
  val: number;
  total: number;
}

function isRuleBreached(
  counter: ReliabilityCounter | null | undefined,
  rule: ReliabilityAutoRestartRule,
): boolean {
  if (!counter || counter.total <= 0 || counter.total < rule.minPresentations) return false;
  return (counter.val * 100) / counter.total >= rule.minErrorPercentage;
}

function toFixationLossCounter(
  snapshot: VfaReliabilitySnapshot | null | undefined,
): ReliabilityCounter | null {
  const total = snapshot?.blindSpotTotal;
  const val = snapshot?.blindSpotSeen;
  if (typeof total !== "number" || typeof val !== "number") return null;
  return { val, total };
}

export function isVfaReliabilityBreached(
  snapshot?: VfaReliabilitySnapshot | null,
  configuration: VisualFieldAutoRestartConfiguration = DEFAULT_ADMIN_CONFIGURATIONS.visualFieldAutoRestart,
): boolean {
  if (!snapshot) return false;
  return (
    isRuleBreached(snapshot.fpFnInfo?.fn, configuration.falseNegative) ||
    isRuleBreached(snapshot.fpFnInfo?.fp, configuration.falsePositive) ||
    isRuleBreached(toFixationLossCounter(snapshot), configuration.fixationLoss)
  );
}

export type VfaAutoRestartCountdownDecision = "none" | "arm" | "cancel" | "restart";

export interface VfaAutoRestartCountdownState {
  reliability?: VfaReliabilitySnapshot | null;
  configuration?: VisualFieldAutoRestartConfiguration;
  secondsRemaining: number | null;
  dismissed: boolean;
  isRunning: boolean;
  isFinished: boolean;
  wasRunning: boolean;
}

export function resolveVfaAutoRestartCountdown({
  reliability,
  configuration,
  secondsRemaining,
  dismissed,
  isRunning,
  isFinished,
  wasRunning,
}: VfaAutoRestartCountdownState): VfaAutoRestartCountdownDecision {
  if (dismissed) {
    return secondsRemaining === null ? "none" : "cancel";
  }

  // The countdown cannot outlive the run it belongs to: a test that reaches the
  // results screen before the countdown expires must still be restarted, so a
  // pending countdown (or a breach seen together with the completion) fires now.
  if (isFinished) {
    if (secondsRemaining !== null) return "restart";
    return wasRunning && isVfaReliabilityBreached(reliability, configuration) ? "restart" : "none";
  }

  if (!isRunning) {
    return secondsRemaining === null ? "none" : "cancel";
  }

  if (secondsRemaining === null) {
    return isVfaReliabilityBreached(reliability, configuration) ? "arm" : "none";
  }

  return secondsRemaining <= 0 ? "restart" : "none";
}
